import { cn } from "@/lib/utils";
import { useState } from "react";
import { createPortal } from "react-dom";

import { Message } from "./message-list";
import MoreIcon from "../ui/icons/MoreIcon";
import { Text } from "../ui/text";

interface MessageNavProps {
  messages: Message[];
  activeIndex?: number; // 현재 보고 있는 질문 인덱스 (스크롤 기준)
  onSelect?: (message: Message, index: number) => void;
}

const MessageNav = ({ messages, activeIndex, onSelect }: MessageNavProps) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [isNavVisible, setIsNavVisible] = useState(false);

  const maxVisibleDots = 5;
  const displayDots = messages.slice(0, maxVisibleDots);
  const hasMoreMessages = messages.length > maxVisibleDots;

  // 6번째 이상(index >= 5)의 메시지에 hover 중인지 확인
  const isHoveringMoreItems =
    hoveredIndex !== null && hoveredIndex >= maxVisibleDots;
  // 현재 보고 있는 질문이 6번째 이상인지 확인
  const isActiveMoreItems =
    activeIndex !== undefined && activeIndex >= maxVisibleDots;

  /* body 로 빼서 그린다. 채팅 본문 래퍼에 container-type 이 걸려 있어 그 안에 두면
     fixed 의 기준이 뷰포트가 아니라 그 래퍼가 된다 */
  return createPortal(
    /* 숨은 목록도 자리를 차지해서, 래퍼 전체를 hover 대상으로 두면 빈 자리에 마우스만 올려도 목록이 뜨고
       뒤 화면 클릭을 가로챈다. 래퍼는 pointer-events-none 으로 통과시키고 점과 목록만 되살린다 */
    <div className="pointer-events-none fixed top-[300px] right-[73px] z-10 flex items-start">
      {/* 메뉴 박스 - 점이나 목록에 마우스가 있을 때만 표시 */}
      <nav
        aria-hidden={!isNavVisible}
        className={cn(
          isNavVisible ? "opacity-100 visible" : "opacity-0 invisible",
          /* 숨을 때도 visibility 가 200ms 늦게 바뀌어 점에서 목록으로 건너가는 사이 닫히지 않는다 */
          "pointer-events-auto mr-2 transition-all duration-200",
          "bg-white rounded-lg shadow-lg border border-gray-200",
          "max-h-[410px] overflow-y-auto"
        )}
        onMouseEnter={() => setIsNavVisible(true)}
        onMouseLeave={() => setIsNavVisible(false)}
      >
        <ul className="flex flex-col p-2">
          {messages?.map((msg, index) => (
            <li key={index} className="px-4">
              <button
                className={cn(
                  "w-[234px] h-10 truncate text-left px-4 py-2",
                  "text-grey-50 hover:text-grey-20",
                  "transition-colors duration-150",
                  index !== messages.length - 1 && "border-b border-gray-100",
                  // 현재 보고 있는 질문 하이라이팅
                  activeIndex === index && "text-grey-20 font-semibold"
                )}
                onClick={() => onSelect?.(msg, index)}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                title={msg.message}
              >
                <Text variant="label-lg" className="text-inherit">
                  {msg.message}
                </Text>
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* Dots - 세로 배치 */}
      <ul
        aria-label="질문 위치"
        className="pointer-events-auto flex flex-col gap-[6px] items-center"
        onMouseEnter={() => setIsNavVisible(true)}
        onMouseLeave={() => setIsNavVisible(false)}
      >
        {displayDots?.map((_, index) => (
          <li
            key={index}
            className="w-4 h-4 flex justify-center items-center cursor-pointer"
          >
            <div
              className={cn(
                "rounded-full w-2 h-2 transition-colors duration-200",
                // 하이라이팅: 현재 보고 있는 질문
                activeIndex === index
                  ? "bg-[#808080]"
                  : hoveredIndex === index
                  ? "bg-[#808080]"
                  : "bg-[#CCCCCC]",
                "hover:bg-[#808080]"
              )}
            />
          </li>
        ))}
        {hasMoreMessages && (
          <li className="flex">
            <MoreIcon
              color={
                isHoveringMoreItems || isActiveMoreItems ? "#808080" : "#CCCCCC"
              }
              size={16}
            />
          </li>
        )}
      </ul>
    </div>,
    document.body
  );
};

export default MessageNav;
